import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { Dashboard } from './components/Dashboard';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Settings } from './pages/Settings';
import { TestCasesPage } from './pages/TestCasesPage';
import { TestCycles } from './pages/TestCycles';
import { CycleReportPage } from './pages/CycleReportPage';
import { StabilityPage } from './pages/StabilityPage';
import { ChatPage } from './pages/ChatPage';
import { AutomationPlannerPage } from './pages/AutomationPlannerPage';
import { AiAnalytics } from './pages/AiAnalytics';
import { PrivacyPolicy } from './components/legal/PrivacyPolicy';
import { useOrganizationFeatures } from './hooks/useOrganizationFeatures';
import { ProjectProvider } from './context/ProjectContext';

function FeatureGatedRoute({
  featureKey,
  aiFeatureKey,
  children,
}: {
  featureKey?: 'testCasesEnabled' | 'testCyclesEnabled';
  aiFeatureKey?: 'rootCauseAnalysis' | 'autoBugGeneration' | 'flakinessDetective' | 'testOptimizer' | 'prRouting' | 'qualityChatbot' | 'automationPlanner';
  children: React.ReactNode;
}) {
  const { features, aiFeatures, isLoading } = useOrganizationFeatures();
  if (isLoading) return null; // avoid flash-redirect while loading
  if (featureKey   && !features[featureKey])     return <Navigate to="/dashboard" replace />;
  if (aiFeatureKey && !aiFeatures[aiFeatureKey]) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function App() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <ProjectProvider>
            <div className="min-h-screen bg-gh-bg-subtle dark:bg-gh-bg-dark text-gh-text dark:text-gh-text-dark font-sans antialiased transition-colors duration-200">
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/join" element={<Signup />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

                {/* Protected routes — share the AppLayout shell */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/test-cases" element={
                    <FeatureGatedRoute featureKey="testCasesEnabled">
                      <TestCasesPage />
                    </FeatureGatedRoute>
                  } />
                  <Route path="/test-cycles" element={
                    <FeatureGatedRoute featureKey="testCyclesEnabled">
                      <TestCycles />
                    </FeatureGatedRoute>
                  } />
                  <Route path="/stability" element={
                    <FeatureGatedRoute aiFeatureKey="flakinessDetective">
                      <StabilityPage />
                    </FeatureGatedRoute>
                  } />
                  <Route path="/chat" element={
                    <FeatureGatedRoute aiFeatureKey="qualityChatbot">
                      <ChatPage />
                    </FeatureGatedRoute>
                  } />
                  <Route path="/automation-planner" element={
                    <FeatureGatedRoute aiFeatureKey="automationPlanner">
                      <AutomationPlannerPage />
                    </FeatureGatedRoute>
                  } />
                  {/* AI FinOps dashboard — no feature-flag gate, always accessible */}
                  <Route path="/ai-analytics" element={<AiAnalytics />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>

                {/* Standalone protected routes — no AppLayout shell */}
                <Route
                  element={
                    <ProtectedRoute>
                      <div className="min-h-screen bg-[#0d1117]"><Outlet /></div>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/test-cycles/:id/report" element={
                    <FeatureGatedRoute featureKey="testCyclesEnabled">
                      <CycleReportPage />
                    </FeatureGatedRoute>
                  } />
                </Route>

                {/* Redirect root to dashboard */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </BrowserRouter>
            </div>
          </ProjectProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
